import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PreviewImage,
  PrimaryButton,
  SearchTextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalFormBank from "./components/ModalFormBank";

function Bank() {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [loadingButton, setLoadingButton] = useState(false);
  const [modalFormVisible, setModalFormVisible] = useState(false);
  const [preload, setPreload] = useState(null);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/bank`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const handleDelete = async id => {
    try {
      await axios.delete(`${hardBaseUrl}/bank/${id}`, {
        headers,
      });
      ModalSuccess("Succesfully delete bank").then(() => getDataTable());
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const handleSubmit = async ({ payload, bankLogo }) => {
    const method = preload ? axios.put : axios.post;
    const modifiedPayload = {
      ...payload,
      ...(bankLogo?.image && { image_logo: bankLogo.image }),
    };
    const formData = new FormData();
    Object.keys(modifiedPayload).forEach(key => {
      if (key !== "profile_image" && key !== "bank_id") {
        formData.append(key, modifiedPayload[key]);
      }
    });
    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/bank${preload ? `/${preload?.bank_id}` : ""}`,
        formData,
        { headers }
      );
      setModalFormVisible(false);
      ModalSuccess(
        `Successfully ${preload ? "Update" : "Create"} Bank`
      ).then(() => getDataTable());
    } catch (error) {
      setModalFormVisible(false);
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingButton(false);
      setPreload(null);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Bank">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <PrimaryButton
              label="Add Bank"
              onClick={() => {
                setModalFormVisible(true);
                setPreload(null);
              }}
              startIcon={<Add />}
              size="large"
              disabled={loadingPage}
            />
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          deleteConfirmation
          deleteConfirmationContent={item =>
            `Are you sure you want to delete bank ${item?.name} ?`
          }
          handleDelete={item => handleDelete(item?.bank_id)}
          handleEdit={item => {
            setModalFormVisible(true);
            setPreload({
              bank_id: item?.bank_id,
              name: item?.name,
              code: item?.code,
              profile_image: item?.profile_image,
            });
          }}
        />
      </Container>

      {modalFormVisible && (
        <ModalFormBank
          open={modalFormVisible}
          onClose={() => setModalFormVisible(false)}
          preload={preload}
          handleSubmit={handleSubmit}
          loadingButton={loadingButton}
        />
      )}
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Parameter",
    link: "/admin/parameter/bank-admin",
  },
  {
    label: "Bank",
    active: true,
  },
];
const columnTable = [
  {
    name: "name",
    title: "Bank Name",
  },
  {
    name: "code",
    title: "Bank Code",
  },
  {
    name: "profile_image",
    title: "Logo",
    renderText: item => <PreviewImage imageUrl={item} />,
  },
];

export default Bank;
